import React, { FC } from 'react'
import { Trans, t } from '@lingui/macro'

import IconPencil from '../../../../components/common/icon/IconPencil'
import IconTrash from '../../../../components/common/icon/IconTrash'

import { EditCampaignFilterModal } from './modals/enrolment-modal/EditCampaignFilterModal'
import { TopicEnrolmentFilterHumanizer } from './filter-humanizers/TopicEnrolmentFilterHumanizer'
import { PathwayEnrolmentFilterHumanizer } from './filter-humanizers/PathwayEnrolmentFilterHumanizer'
import { GroupFilterHumanizer } from './filter-humanizers/GroupFilterHumanizer'
import {
  CampaignFilterTypeEnum,
  CampaignSearchableUserAttribute
} from 'shared/enums/campaigns'
import { CertificationEnrolmentFilterHumanizer } from './filter-humanizers/CertificationEnrolmentFilterHumanizer'

import { CampaignFilter } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useCachedWorkspacesQuery } from 'front-end/src/app/stores/useCachedWorkspacesQuery'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { ButtonIcon } from 'front-end/src/components'
import { SingleCampaign } from 'front-end/src/pages/manage-campaign-page/ManageCampaignPage'
import { ATTRIBUTE_COMPARATOR_MAP } from 'shared/userFilterConstants'
import { EventInvitationFilterHumanizer } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/filter-humanizers/EventInvitationFilterHumanizer'

export const filterDescriptionMapper = {
  [CampaignFilterTypeEnum.duration_comparison]: (filter) => {
    return (
      <>
        <Trans>
          Has not been active for{' '}
          <strong>{filter.comparator.value.days} days</strong>
        </Trans>
      </>
    )
  },
  [CampaignFilterTypeEnum.group]: GroupFilterHumanizer,
  [CampaignFilterTypeEnum.workspace]: (filter) => {
    const { data } = useCachedWorkspacesQuery()
    const workspaces = data?.workspaces

    const filterWorkspaces = workspaces
      ? workspaces.filter((w) => filter.ref_ids.includes(w.id))
      : []
    const workspacesNames = (
      <>
        {filterWorkspaces.map((g, index) => {
          return (
            <span key={g.name}>
              <strong>{g.name}</strong>
              {index < filterWorkspaces.length - 1 ? ' or ' : ''}
            </span>
          )
        })}
      </>
    )

    return <Trans>Is member of the {workspacesNames} workspace</Trans>
  },
  [CampaignFilterTypeEnum.value_comparison]: (filter) => {
    const OPERATOR_TRANSLATION = {
      [ATTRIBUTE_COMPARATOR_MAP.in]: t`is`,
      [ATTRIBUTE_COMPARATOR_MAP['not in']]: t`is not`,
      [ATTRIBUTE_COMPARATOR_MAP.contains]: t`contains`,
      [ATTRIBUTE_COMPARATOR_MAP.is_empty]: t`is empty`
    }
    const ATTRIBUTE_TRANSLATION = {
      [CampaignSearchableUserAttribute.email]: t`Email`,
      [CampaignSearchableUserAttribute.account_name]: t`Account name`,
      [CampaignSearchableUserAttribute.display_name]: t`Display name`,
      [CampaignSearchableUserAttribute.first_name]: t`First name`,
      [CampaignSearchableUserAttribute.last_name]: t`Last name`,
      [CampaignSearchableUserAttribute.last_name]: t`Last name`,
      [CampaignSearchableUserAttribute.position]: t`Position`,
      [CampaignSearchableUserAttribute.timezone]: t`Timezone`,
      [CampaignSearchableUserAttribute.employee_id]: t`Employee id`,
      [CampaignSearchableUserAttribute.area]: t`Area`,
      [CampaignSearchableUserAttribute.location]: t`Location`,
      [CampaignSearchableUserAttribute.department]: t`Department`,
      [CampaignSearchableUserAttribute.custom_1]: t`Custom 1`,
      [CampaignSearchableUserAttribute.custom_2]: t`Custom 2`,
      [CampaignSearchableUserAttribute.custom_3]: t`Custom 3`,
      [CampaignSearchableUserAttribute.phone_number]: t`Phone number`,
      [CampaignSearchableUserAttribute.manager_email]: t`Manager email`,
      [CampaignSearchableUserAttribute.locale]: t`Language`
    }
    const isArrayLike = Array.isArray(filter.comparator.value)
    const operator = OPERATOR_TRANSLATION[filter.comparator.operator]
    const attribute = ATTRIBUTE_TRANSLATION[filter.comparator.field]

    return (
      <>
        <Trans>
          User's {attribute} {operator}{' '}
          <strong>
            {isArrayLike
              ? filter.comparator.value.join(', ')
              : filter.comparator.value}
          </strong>
        </Trans>
      </>
    )
  },
  [CampaignFilterTypeEnum.pathway_enrolment]: PathwayEnrolmentFilterHumanizer,
  [CampaignFilterTypeEnum.topic_enrolment]: TopicEnrolmentFilterHumanizer,
  [CampaignFilterTypeEnum.certification_enrolment]:
    CertificationEnrolmentFilterHumanizer,
  [CampaignFilterTypeEnum.event_invitation]: EventInvitationFilterHumanizer
}

const CampaignFiltersList: FC<{
  filters: SingleCampaign['filters']
  onUpdate: (filters: SingleCampaign['filters']) => any
}> = ({ filters, onUpdate }) => {
  const { open, currentModal } = useModal()

  if (filters.length === 0) {
    return null
  }

  return (
    <>
      {
        filters
          .map((filter, idx: number) => {
            const FilterHumanized = filterDescriptionMapper[filter.type]
            return (
              <div className="Filter" key={idx}>
                <div className="Filter__description">
                  <FilterHumanized {...(filter as CampaignFilter)} />
                </div>
                <div className="Filter__controls">
                  <ButtonIcon
                    circular
                    key="edit"
                    icon={<IconPencil />}
                    aria-label={t`Edit`}
                    hint={t`Edit`}
                    color="medium"
                    onClick={() =>
                      open(EditCampaignFilterModal, {
                        editedFilter: filter
                      }).then((updatedFilter) =>
                        onUpdate(
                          Object.assign(filters.slice(), {
                            [idx]: updatedFilter
                          })
                        )
                      )
                    }
                  />
                  <ButtonIcon
                    circular
                    key="delete"
                    icon={<IconTrash />}
                    aria-label={t`Delete`}
                    hint={t`Delete`}
                    color="medium"
                    onClick={() => {
                      onUpdate(filters.filter((f) => f !== filter))
                    }}
                  />
                </div>
              </div>
            )
          })
          .map((e, i) =>
            i < filters.length - 1
              ? [
                  e,
                  <span key={`${i}and`} className="Filters__logic-operation">
                    <Trans>AND</Trans>
                  </span>
                ]
              : [e]
          ) // copied from https://stackoverflow.com/a/48770770/671457
          .reduce((a, b) => a.concat(b)) as any // TODO TS fix this?
      }
      {currentModal}
    </>
  )
}

export default CampaignFiltersList
